import { useMemo } from 'react';

import { UserPermission, useGetUsersByGroupsQuery } from 'data/__generated__';

import { Option, TotemSelect, TotemSelectProps } from 'components/TotemSelect';

export const INTERNAL_USER_GROUPS: UserPermission[] = [
    UserPermission.Toteam,
    UserPermission.Totech,
    UserPermission.Totest,
    UserPermission.Deliverer,
    UserPermission.ExternalDeliverer,
    UserPermission.Preparer,
];

type UserSelectBaseProps = {
    userGroups?: UserPermission[];
    label?: string;
    placeholder?: string;
    dataTest?: string;
    width?: string;
    isClearable?: boolean;
    disabled?: boolean;
};

type UserSelectSingleProps = UserSelectBaseProps & {
    value: string | null;
    onChange: (userId: string | null) => void;
    isMulti?: false;
};

type UserSelectMultiProps = UserSelectBaseProps & {
    value: string[];
    onChange: (userIds: string[]) => void;
    isMulti: true;
};

export type UserSelectProps = UserSelectSingleProps | UserSelectMultiProps;

export function UserSelect(props: UserSelectProps) {
    const {
        userGroups = INTERNAL_USER_GROUPS,
        label = 'Choisir un utilisateur',
        placeholder = 'Sélectionner un utilisateur',
        isClearable = true,
    } = props;

    const { data } = useGetUsersByGroupsQuery({
        variables: { userGroups },
        fetchPolicy: 'cache-and-network',
    });

    const options = useMemo<Option<string>[]>(
        () =>
            (data?.usersByGroups ?? [])
                .map((user) => ({
                    value: user._id,
                    label: user.profile?.fullname ? `${user.profile.fullname} (${user.username})` : user.username,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [data],
    );

    const commonProps = {
        label,
        placeholder,
        dataTest: props.dataTest,
        width: props.width,
        isClearable,
        disabled: props.disabled,
        options,
    };

    if (props.isMulti) {
        return (
            <TotemSelect<string>
                {...(commonProps as Extract<TotemSelectProps<string>, { isMulti: true }>)}
                isMulti
                value={props.value}
                onChange={props.onChange}
            />
        );
    }

    return (
        <TotemSelect<string>
            {...(commonProps as Extract<TotemSelectProps<string>, { isMulti?: false }>)}
            value={props.value}
            onChange={props.onChange}
        />
    );
}
